import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
	setActiveTeamConversation,
	getSurroundingMessages,
	getPreviousTeamConversations,
} from '../../../actions/teams';
import getEarliestTeamConversation from '../../utils/getEarliestTeamConversation';
import { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

// The thread renders after the URL changes (on phones it is a new screen), so
// wait for the message to appear before scrolling to it.
const scroll = (messageId, timeout = 3000) => {
	const startedAt = Date.now();
	const tryScroll = () => {
		const section = document.getElementById(messageId);
		if (section) {
			section.scrollIntoView({ behavior: 'smooth', block: 'start' });
			return;
		}
		if (Date.now() - startedAt < timeout) {
			requestAnimationFrame(tryScroll);
		}
	};
	tryScroll();
};

const TeamSearchResults = ({
	setCustomerConversationAssignedType,
	searchResults,
	setSearchResults,
	searchText,
	setIsSearching,
}) => {
	const teams = useSelector((state) => state.teams);
	const dispatch = useDispatch();
	const store = useStore();
	const navigate = useNavigate();
	// Phones open the result as a new screen; keep the results so Back
	// returns to them, as in Mail and Messages.
	const isMobile = useIsMobile();

	const handleClose = () => {
		setIsSearching(false);
		if (!isMobile) {
			setSearchResults([]);
		}
	};

	const handleSearchResultClick = async (result) => {
		setIsSearching(true);

		// Check if the conversation that is to be loaded exists in the current Redux state
		const conversationExists = teams.teamConversations.some(
			(conversation) => conversation._id === result._id
		);

		if (!conversationExists) {
			const earliestConversation = getEarliestTeamConversation({
				isDirectMessage: result.isDirectMessage,
				teamConversations: teams.teamConversations,
			});

			const earliestConversationDate = earliestConversation
				? new Date(earliestConversation.latestMessageDate)
				: new Date();

			await dispatch(
				getPreviousTeamConversations({
					loadConversationsBefore: earliestConversationDate,
					_loadConversationUpTillThisTeamConversation: result._id,
					loadConversationsForGroups: !result.isDirectMessage,
				})
			);
		}

		// Dispatch actions for other tasks
		await dispatch(
			getSurroundingMessages({
				_teamMessage: result.text._id,
				_teamConversation: result._id,
			})
		);
		dispatch(setActiveTeamConversation(result._id));
		setCustomerConversationAssignedType(
			result.isDirectMessage ? 'DIRECT_MESSAGES' : 'GROUPS'
		);

		// Open it through the URL like a list row, so the address, Back and
		// the phone's list → thread stack all follow. _target opens a phone
		// thread at the result (TeamMessages).
		const isDirectMessage = Boolean(result.isDirectMessage);
		const conversation = store
			.getState()
			.teams.teamConversations.find((tc) => tc._id === result._id);
		const _business = conversation?._business || result._business || '';
		navigate(
			`/groupchat/${isDirectMessage ? 'dms' : 'groups'}/conversation/groups/${result._id}?_location=${_business}&_teamMessage=${result.text._id}&_target=${result.text._id}&isDirectMessage=${isDirectMessage}`
		);

		scroll(result.text._id);
		handleClose();
	};

	const highlightMatches = (text) => {
		// get the index of where the highlighting should start
		const index = text.toLowerCase().indexOf(searchText.toLowerCase());

		if (index === -1) {
			return text;
		}

		// get text before highlight
		const start = text.slice(0, index).split('');

		// get highlighted text
		const highlight = text
			.slice(index, index + searchText.length)
			.split('');

		// get text after highlight
		const end = text.slice(start.length + highlight.length).split('');

		const startSpan = start.map((t, i) => (
			<span key={`${t}${i}`}>{t}</span>
		));

		const highlightSpan = highlight.map((t, i) => (
			<span
				key={`${i}${t}`}
				style={{
					background: '#ffd50080',
					borderRadius: '5%',
				}}
			>
				{t}
			</span>
		));
		const endSpan = end.map((t, i) => <span key={`${t}-${i}`}>{t}</span>);

		const all = [...startSpan, ...highlightSpan, ...endSpan];

		return all;
	};

	useEffect(() => {
		if (searchText.length === 0) {
			setIsSearching(false);
			setSearchResults([]);
		}
	}, [searchText]);

	return (
		<div className="flex flex-col gap-2 p-2 py-0 my-2">
			{searchResults.length === 0 && (
				<p className="flex justify-center items-center text-center font-semibold ">
					No Results
				</p>
			)}
			{searchResults.map((result) => {
				return (
					<button
						className={
							'flex flex-col items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent'
						}
						onClick={() => handleSearchResultClick(result)}
						key={result.text?._id || result._id}
					>
						<div className="flex flex-col space-y-1">
							<p className="font-semibold">{result.title}</p>
							<p className="text-foreground text-xs font-medium">
								{result.text._user.name}
							</p>
							<p
								className="text-xs text-muted-foreground text-ellipsis line-clamp-2 text-wrap break-words"
								style={{
									wordBreak: 'break-word',
								}}
							>
								{highlightMatches(result.text.text)}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
};

export default TeamSearchResults;
